import { useMemo, useState } from "react";
import PatientAlias from "./PatientAlias.jsx";

const PX_PER_MIN = 2; // 120px/час
const GUTTER_W = 56;
const COL_W = 260;
const MIN_BLOCK_H = 32;
const DEFAULT_RANGE = [9 * 60, 19 * 60]; // 9:00–19:00, когда на день вообще нет записей

const PALETTE = [
  { bg: "#e0f2f1", fg: "#00695c" },
  { bg: "#ede7f6", fg: "#4527a0" },
  { bg: "#fff3e0", fg: "#e65100" },
  { bg: "#e3f2fd", fg: "#1565c0" },
  { bg: "#fce4ec", fg: "#ad1457" },
];

const AVATAR_COLORS = ["bg-blue-100 text-blue-700", "bg-purple-100 text-purple-700", "bg-emerald-100 text-emerald-700", "bg-amber-100 text-amber-700", "bg-pink-100 text-pink-700"];

function parseMinutes(dateStr) {
  const timePart = (dateStr || "").split(" ")[1] || "00:00:00";
  const [h, m] = timePart.split(":").map(Number);
  return h * 60 + (m || 0);
}

function formatClock(totalMin) {
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

const floor30 = (m) => Math.floor(m / 30) * 30;
const ceil30 = (m) => Math.ceil(m / 30) * 30;

// Фото сотрудника из YClients (у неё всегда есть плейсхолдер, даже без реального фото) —
// при ошибке загрузки (сеть/CORS) тихо откатываемся на инициалы, без сломанной иконки.
function StaffAvatar({ name, avatar, colorClass }) {
  const [failed, setFailed] = useState(false);
  if (avatar && !failed) {
    return (
      <img
        src={avatar}
        alt=""
        onError={() => setFailed(true)}
        className="h-8 w-8 rounded-full object-cover flex-shrink-0 bg-slate-100"
      />
    );
  }
  return (
    <div className={`h-8 w-8 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0 ${colorClass}`}>
      {name.slice(0, 2).toUpperCase()}
    </div>
  );
}

// Жадная раскладка пересекающихся по времени записей одного сотрудника в дорожки (лейны) — редкий
// случай двойной записи не должен схлопывать блоки друг на друга.
function layoutLanes(records) {
  const sorted = [...records].sort((a, b) => a._start - b._start);
  const laneEnds = [];
  for (const r of sorted) {
    let lane = laneEnds.findIndex((end) => end <= r._start);
    if (lane === -1) { lane = laneEnds.length; laneEnds.push(r._end); }
    else laneEnds[lane] = r._end;
    r._lane = lane;
  }
  const laneCount = laneEnds.length || 1;
  for (const r of sorted) r._laneCount = laneCount;
  return sorted;
}

export default function JournalCalendar({ staffList, records, onSelectRecord, isToday }) {
  const { columns, rangeStart, rangeEnd, gridLines, nowTop } = useMemo(() => {
    const withTimes = records.map((r) => {
      const start = parseMinutes(r.date);
      const durationMin = r.seanceLength ? r.seanceLength / 60 : 30;
      return { ...r, _start: start, _end: start + durationMin };
    });

    let rangeStart, rangeEnd;
    if (withTimes.length) {
      rangeStart = floor30(Math.min(...withTimes.map((r) => r._start))) - 30;
      rangeEnd = ceil30(Math.max(...withTimes.map((r) => r._end))) + 30;
    } else {
      [rangeStart, rangeEnd] = DEFAULT_RANGE;
    }
    if (rangeEnd - rangeStart < 60) rangeEnd = rangeStart + 60;

    const staffIds = new Set(staffList.map((s) => s.id));
    const byStaff = new Map(staffList.map((s) => [s.id, []]));
    const orphans = [];
    for (const r of withTimes) {
      if (r.staffId != null && byStaff.has(r.staffId)) byStaff.get(r.staffId).push(r);
      else orphans.push(r);
    }

    // Только сотрудники с хотя бы одной записью на этот день — пустые колонки не показываем.
    const cols = staffList
      .filter((s) => (byStaff.get(s.id) || []).length > 0)
      .map((s) => ({
      id: s.id,
      name: s.name,
      specialization: s.specialization,
      avatar: s.avatar,
      records: layoutLanes(byStaff.get(s.id) || []).map((r) => ({
        ...r,
        _top: (r._start - rangeStart) * PX_PER_MIN,
        _height: Math.max((r._end - r._start) * PX_PER_MIN, MIN_BLOCK_H),
      })),
    }));
    if (orphans.length) {
      cols.push({
        id: "__unassigned",
        name: "Без сотрудника",
        specialization: null,
        records: layoutLanes(orphans).map((r) => ({
          ...r,
          _top: (r._start - rangeStart) * PX_PER_MIN,
          _height: Math.max((r._end - r._start) * PX_PER_MIN, MIN_BLOCK_H),
        })),
      });
    }

    const lines = [];
    for (let t = Math.ceil(rangeStart / 20) * 20; t <= rangeEnd; t += 20) {
      lines.push({ top: (t - rangeStart) * PX_PER_MIN, isHour: t % 60 === 0, label: t % 60 === 0 ? formatClock(t) : String(t % 60) });
    }

    let nowTop = null;
    if (isToday) {
      const now = new Date();
      const nowMin = now.getHours() * 60 + now.getMinutes();
      if (nowMin >= rangeStart && nowMin <= rangeEnd) nowTop = (nowMin - rangeStart) * PX_PER_MIN;
    }

    return { columns: cols, rangeStart, rangeEnd, gridLines: lines, nowTop };
  }, [staffList, records, isToday]);

  const gridHeight = (rangeEnd - rangeStart) * PX_PER_MIN;
  const totalWidth = GUTTER_W + columns.length * COL_W;
  const isEmpty = columns.length === 0;

  if (isEmpty) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-2 py-16 text-center text-slate-400">
        <p className="text-sm font-semibold text-slate-700">Нет активных сотрудников и записей на этот день</p>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto rounded-2xl border border-slate-200 bg-white">
      <div
        className="grid"
        style={{ width: totalWidth, gridTemplateColumns: `${GUTTER_W}px repeat(${columns.length}, ${COL_W}px)`, gridTemplateRows: `52px ${gridHeight}px` }}
      >
        {/* левый верхний угол — закреплён и по вертикали, и по горизонтали */}
        <div className="sticky top-0 left-0 z-30 bg-white border-b border-r border-slate-100" style={{ gridColumn: 1, gridRow: 1 }} />

        {/* заголовки колонок — закреплены сверху. Явный gridColumn у каждой ячейки обязателен: без
            него авто-раскладка после явно спозиционированного оверлея линий (ниже) уезжает в
            неявную третью строку — именно так проявлялся баг "блоков не видно". */}
        {columns.map((col, i) => (
          <div key={col.id} className="sticky top-0 z-20 bg-white border-b border-slate-100 px-2.5 py-2 flex items-center gap-2 overflow-hidden" style={{ gridColumn: i + 2, gridRow: 1 }}>
            <StaffAvatar name={col.name} avatar={col.avatar} colorClass={AVATAR_COLORS[i % AVATAR_COLORS.length]} />
            <div className="min-w-0">
              <div className="text-xs font-semibold text-slate-800 truncate">{col.name}</div>
              {col.specialization && <div className="text-[10px] text-slate-400 truncate">{col.specialization}</div>}
            </div>
          </div>
        ))}

        {/* линейка времени — закреплена слева */}
        <div className="sticky left-0 z-10 bg-white border-r border-slate-100 relative" style={{ gridColumn: 1, gridRow: 2, height: gridHeight }}>
          {gridLines.map((g, i) => (
            <div
              key={i}
              className={`absolute right-1.5 ${g.isHour ? "text-[11px] font-semibold text-slate-500" : "text-[9px] text-slate-300"}`}
              style={{ top: g.top - 6 }}
            >
              {g.label}
            </div>
          ))}
        </div>

        {/* сетка линий под колонками — единый слой на всю ширину, чтобы линии не расходились между колонками */}
        <div className="relative z-0" style={{ gridColumn: `2 / -1`, gridRow: 2, height: gridHeight, pointerEvents: "none" }}>
          {gridLines.map((g, i) => (
            <div key={i} className={`absolute left-0 right-0 border-t ${g.isHour ? "border-slate-200" : "border-slate-100"}`} style={{ top: g.top }} />
          ))}
          {nowTop != null && (
            <div className="absolute left-0 right-0 border-t-2 border-red-400" style={{ top: nowTop }} />
          )}
        </div>

        {/* сами колонки с блоками записей — рисуются поверх линий (идут в разметке позже) */}
        {columns.map((col, i) => (
          <div key={col.id} className="relative z-10 border-r border-slate-50 last:border-r-0" style={{ gridColumn: i + 2, gridRow: 2, height: gridHeight }}>
            {col.records.map((r) => {
              const palette = PALETTE[Math.abs(hashCode(r.id)) % PALETTE.length];
              return (
                <div
                  key={r.id}
                  onClick={() => onSelectRecord(r)}
                  className="absolute rounded-lg px-1.5 py-1 overflow-hidden cursor-pointer text-[11px] leading-tight shadow-sm ring-1 ring-black/5 hover:shadow-md hover:ring-black/10 hover:z-20 transition-shadow"
                  style={{
                    top: r._top,
                    height: r._height,
                    left: `calc(${r._lane} * 100% / ${r._laneCount} + 2px)`,
                    width: `calc(100% / ${r._laneCount} - 4px)`,
                    background: r.customColor || palette.bg,
                    color: r.customFontColor || palette.fg,
                  }}
                >
                  <div className="flex items-center gap-1 font-semibold">
                    <span className="truncate min-w-0">{formatClock(r._start)} {r.clientName}<PatientAlias name={r.clientName} alias={r.scanAppPatientAlias} className="text-inherit opacity-70" /></span>
                    {r.clientIsNew && (
                      <span className="flex-shrink-0 text-[8px] font-bold uppercase tracking-wide bg-white/70 rounded px-1 py-px">new</span>
                    )}
                  </div>
                  <div className="truncate opacity-80">{r.services?.length ? r.services.join(", ") : "Без услуги"}</div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

// Стабильный хэш id записи -> индекс палитры (id может быть строкой или числом в зависимости от источника).
function hashCode(id) {
  const s = String(id);
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return h;
}
